import React from 'react';
import Input from '../../../components/ui/Input';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';


const GRADE_LEVELS = [
  'All Grades',
  'Primary 1',
  'Primary 2',
  'Primary 3',
  'Primary 4',
  'Primary 5',
  'Primary 6',
];

const ConfigurationStep = ({ formData, onChange, onImageUpload, errors }) => {

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (file?.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        onImageUpload(reader?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-sm">
        <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-4 md:mb-6">
          Trail Configuration
        </h2>
        <p className="text-sm md:text-base text-muted-foreground mb-6 md:mb-8">
          Set up the basic information for your learning trail
        </p>

        <div className="space-y-6">
          <Input
            label="Trail Title"
            type="text"
            id="trail-title"
            name="title"
            autoComplete="off"
            placeholder="Enter a descriptive title for your learning trail"
            value={formData?.title}
            onChange={(e) => onChange('title', e?.target?.value)}
            error={errors?.title}
            required
            description="Choose a clear, engaging title that describes the learning objective"
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Trail Cover Image <span className="text-error">*</span>
            </label>
            <p className="text-xs md:text-sm text-muted-foreground mb-3">
              Upload an engaging image that represents your learning trail (Max 5MB)
            </p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              <div>
                <input
                  type="file"
                  id="journey-image"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label
                  htmlFor="journey-image"
                  className="flex flex-col items-center justify-center w-full h-48 md:h-56 lg:h-64 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary transition-organic bg-muted/30"
                >
                  <Icon name="Upload" size={32} className="text-muted-foreground mb-3" />
                  <span className="text-sm md:text-base text-muted-foreground text-center px-4">
                    Click to upload or drag and drop
                  </span>
                  <span className="text-xs text-muted-foreground mt-2">
                    PNG, JPG, WEBP (max 5MB)
                  </span>
                </label>
              </div>

              {formData?.image && (
                <div className="relative w-full h-48 md:h-56 lg:h-64 rounded-lg overflow-hidden bg-muted">
                  <Image
                    src={formData?.image}
                    alt="Preview of uploaded learning trail cover image showing educational content"
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => onImageUpload('')}
                    className="absolute top-2 right-2 p-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-organic active-press"
                    aria-label="Remove image"
                  >
                    <Icon name="X" size={18} />
                  </button>
                </div>
              )}
            </div>
            {errors?.image && (
              <p className="text-sm text-error mt-2">{errors?.image}</p>
            )}
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-foreground mb-2">
              Trail Description <span className="text-error">*</span>
            </label>
            <p className="text-xs md:text-sm text-muted-foreground mb-3">
              Provide a detailed description of what students will learn
            </p>
            <textarea
              id="description"
              name="description"
              rows={6}
              placeholder="Describe the learning objectives, key concepts, and expected outcomes..."
              value={formData?.description}
              onChange={(e) => onChange('description', e?.target?.value)}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 focus:ring-offset-background transition-organic resize-none"
            />
            {errors?.description && (
              <p className="text-sm text-error mt-2">{errors?.description}</p>
            )}
          </div>

          {/* Minimum Grade Level */}
          <div>
            <label htmlFor="min-grade-level" className="block text-sm font-medium text-foreground mb-2">
              Minimum Grade Level
            </label>
            <p className="text-xs md:text-sm text-muted-foreground mb-3">
              Students must be at or above this grade to access the trail. Select "All Grades" to allow everyone.
            </p>
            <select
              id="min-grade-level"
              value={formData?.minGradeLevel || 'All Grades'}
              onChange={(e) => onChange('minGradeLevel', e?.target?.value)}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 focus:ring-offset-background transition-organic"
            >
              {GRADE_LEVELS?.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="bg-primary/5 rounded-lg p-4 md:p-6 border border-primary/20">
        <div className="flex items-start">
          <Icon name="Lightbulb" size={24} className="text-primary mr-3 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
              Pro Tips for Configuration
            </h3>
            <ul className="text-sm md:text-base text-muted-foreground space-y-2">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Use action-oriented titles that spark curiosity</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Choose vibrant, high-quality images that relate to the content</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Set the minimum grade level to ensure age-appropriate content access</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationStep;